import { useState, useRef, useEffect, useCallback } from 'react';
import { useStore } from '../services/store';
import type { ShotItem } from '../services/store';
import {
  Image as ImageIcon, Trash2, PlayCircle, CheckSquare, Square,
  RefreshCw, Merge, LayoutGrid, ChevronRight, AlertCircle, ExternalLink, X, Loader2
} from 'lucide-react';
import api, { extractErrorMessage } from '../services/api';
import { generateReferenceVideo, generationEngine, hasReferenceInput, nearestOmniDuration, type RpcEngine } from '../services/referenceVideo';
import { mergeReferenceShots } from '../services/videoMerge';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FlowboardRequest {
  id: number | string;
  type: string;
  status: string;
  created_at: string;
  error?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const AR_LABELS: Record<string, string> = {
  '9:16': 'IMAGE_ASPECT_RATIO_PORTRAIT',
  '16:9': 'IMAGE_ASPECT_RATIO_LANDSCAPE',
  '1:1':  'IMAGE_ASPECT_RATIO_SQUARE',
  '4:3':  'IMAGE_ASPECT_RATIO_LANDSCAPE',
};

// Flowkit only has VERTICAL/HORIZONTAL orientation — no native square/4:3
// support. 1:1 and 4:3 are coerced onto the nearest orientation (same lossy
// tradeoff Flowboard already makes via AR_LABELS/AR_MAP above).
const FK_ORIENTATION: Record<string, 'VERTICAL' | 'HORIZONTAL'> = {
  '9:16': 'VERTICAL', '1:1': 'VERTICAL', '16:9': 'HORIZONTAL', '4:3': 'HORIZONTAL',
};

const CHARACTER_UGC_ENGINE_ERROR = 'Character UGC requires Flowkit or Flowboard native reference video. Select a supported engine explicitly.';

function normalPanelGenerationPrompt(shot: ShotItem): string {
  const prompt = shot.prompt.trim();
  if (shot.characterUgcVersion) return prompt;
  const voiceover = shot.voiceover.trim();
  return [
    prompt,
    voiceover && !/(?:^|\n)\s*Voiceover\s*:/i.test(prompt) ? `Voiceover: ${voiceover}` : '',
    !/(?:^|\n)\s*Duration\s*:/i.test(prompt) ? `Duration: ${shot.durationSec}s` : '',
  ].filter(Boolean).join('\n');
}

export function autoGenerationShots(jobId: string, shots: ShotItem[]): ShotItem[] {
  return shots.filter(shot => shot.jobId === jobId && shot.selected
    && (shot.status === 'pending' || shot.status === 'ready'));
}

function characterContinuityPrompt(name: string, description: string, profile?: Record<string, unknown>): string {
  const appearance = typeof profile?.appearance === 'string' && profile.appearance.trim()
    ? profile.appearance.trim()
    : description.trim();
  const bodyAndSilhouette = typeof profile?.bodyAndSilhouette === 'string' && profile.bodyAndSilhouette.trim()
    ? profile.bodyAndSilhouette.trim()
    : appearance;
  const outfitLock = typeof profile?.outfitLock === 'string' && profile.outfitLock.trim()
    ? profile.outfitLock.trim()
    : appearance;
  const consistencyLock = typeof profile?.consistencyLock === 'string' && profile.consistencyLock.trim()
    ? profile.consistencyLock.trim()
    : description.trim();
  return [
    `Character continuity lock: ${name}.`,
    `Use the canonical ${name} character reference image as the exact face and identity source.`,
    `Body and silhouette: ${bodyAndSilhouette}`,
    `Outfit lock from NotebookLM: ${outfitLock}`,
    `Continuity: ${consistencyLock}`,
    'Keep the same face, apparent age, body proportions, hairstyle, outfit, accessories, and styling. Never recast, redesign, recolor, or replace this character.',
  ].filter(Boolean).join('\n');
}

// ─── ShotCard component ───────────────────────────────────────────────────────

function ShotCard({
  shot, index, onUpdate, onRemove, onGenerate, ugcMode,
}: {
  shot: ShotItem;
  index: number;
  onUpdate: (id: string, patch: Partial<ShotItem>) => void;
  onRemove: (id: string) => void;
  onGenerate: (shot: ShotItem) => void;
  ugcMode?: 'faceless' | 'character';
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const job = useStore(s => s.mpJobs.find(j => j.id === shot.jobId));
  const engine = useStore(s => s.mpEngine);
  const hasReferences = engine === 'antigravity' ? shot.imageDataUrls.length > 0 : hasReferenceInput(shot, job, engine);

  const handleImageAdd = (files: FileList | null) => {
    if (!files?.length) return;
    Promise.all(
      Array.from(files).map(f => new Promise<{ url: string; name: string }>(resolve => {
        const reader = new FileReader();
        reader.onload = e => resolve({ url: e.target?.result as string, name: f.name });
        reader.readAsDataURL(f);
      }))
    ).then(results => {
      onUpdate(shot.id, {
        imageDataUrls: [...shot.imageDataUrls, ...results.map(r => r.url)],
        imageFiles:    [...shot.imageFiles,    ...results.map(r => r.name)],
        status: 'ready',
      });
    });
  };

  const removeImage = (i: number) => {
    onUpdate(shot.id, {
      imageDataUrls: shot.imageDataUrls.filter((_, idx) => idx !== i),
      imageFiles: shot.imageFiles.filter((_, idx) => idx !== i),
    });
  };

  const statusColors: Record<string, string> = {
    pending:    'bg-[#e9d6aa]/60 text-[#6d5b3f]',
    ready:      'bg-[#865916]/15 text-[#865916]',
    uploading:  'bg-[#258cc0]/15 text-[#258cc0]',
    generating: 'bg-[#258cc0]/15 text-[#258cc0]',
    done:       'bg-[#76a441]/15 text-[#476b24]',
    error:      'bg-[#dc553c]/15 text-[#8c2d1f]',
  };

  return (
    <div className={`rounded-xl border transition-all ${
      shot.selected ? 'border-[#865916]/60 shadow-md' : 'border-[#d6bd7c]/50'
    } bg-white overflow-hidden`}>
      {/* Card header */}
      <div className={`flex items-center gap-2 px-3 py-2 border-b border-[#d6bd7c]/30 ${shot.selected ? 'bg-[#fdf6e9]' : 'bg-[#f9f4ec]'}`}>
        <button onClick={() => onUpdate(shot.id, { selected: !shot.selected })} className="shrink-0">
          {shot.selected
            ? <CheckSquare className="w-4 h-4 text-[#865916]" />
            : <Square className="w-4 h-4 text-[#a89470]" />
          }
        </button>
        <span className="text-[11px] font-black tracking-[0.08em] text-[#865916]">PROMPT #{index + 1}</span>
        {shot.boardId && (
          <a href={`/flowboard/#board=${shot.boardId}`} target="_blank" rel="noopener noreferrer"
            className="text-[10px] text-[#258cc0] hover:underline flex items-center gap-0.5 ml-1">
            Board <ExternalLink className="w-2.5 h-2.5" />
          </a>
        )}
        <div className="flex-1" />
        <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold ${statusColors[shot.status] || statusColors.pending}`}>
          {shot.status === 'generating' || shot.status === 'uploading'
            ? <span className="flex items-center gap-1"><Loader2 className="w-2.5 h-2.5 animate-spin" />{shot.status === 'uploading' ? 'queued' : shot.status}</span>
            : shot.status === 'pending' && !shot.selected
              ? 'awaiting selection'
            : shot.status}
        </span>
        <span className="text-[9px] text-[#a89470] font-bold">{shot.durationSec}s</span>
        <button onClick={() => onRemove(shot.id)} className="rounded p-0.5 hover:bg-[#dc553c]/10 text-[#a89470] hover:text-[#8c2d1f]">
          <X className="w-3 h-3" />
        </button>
      </div>

      <div className="p-3 space-y-2">
        {/* Prompt textarea */}
        <textarea
          value={shot.prompt}
          onChange={e => onUpdate(shot.id, { prompt: e.target.value })}
          rows={8}
          placeholder="Enter visual prompt for Google Flow…"
          className="w-full rounded-lg border border-[#d6bd7c] bg-[#fff8ec] px-2.5 py-2 text-xs text-[#2f2414] placeholder:text-[#b8a07a] focus:outline-none focus:ring-1 focus:ring-[#865916]/40 resize-none leading-relaxed"
        />

        {/* Image slots row */}
        <div className="flex items-center gap-2 flex-wrap">
          {shot.imageDataUrls.map((url, i) => (
            <div key={i} className="relative group">
              <img src={url} alt="" className="w-16 h-16 rounded-lg object-cover border border-[#d6bd7c]" />
              <button
                onClick={() => removeImage(i)}
                className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-[#dc553c] text-white hidden group-hover:flex items-center justify-center"
              >
                <X className="w-2.5 h-2.5" />
              </button>
            </div>
          ))}
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
            onChange={e => handleImageAdd(e.target.files)} />
          <button
            onClick={() => fileRef.current?.click()}
            className="flex flex-col items-center justify-center w-16 h-16 rounded-lg border-2 border-dashed border-[#d6bd7c] hover:border-[#865916] hover:bg-[#fdf6e9] transition-colors text-[#a89470] hover:text-[#865916]"
          >
            <ImageIcon className="w-5 h-5" />
            <span className="text-[9px] font-bold mt-0.5">+ Ảnh</span>
          </button>
        </div>

        {/* Error */}
        {(shot.status === 'error' || job?.characterUgc || shot.characterUgcVersion) && shot.errorMsg && (
          <p className="text-[10px] text-[#8c2d1f] flex items-start gap-1">
            <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />{shot.errorMsg}
          </p>
        )}

        {/* Action: Generate */}
        <button
          onClick={() => onGenerate(shot)}
          disabled={shot.status === 'generating' || shot.status === 'uploading'}
          title={hasReferences ? 'Omni Flash reference video — uploaded images are reused' : 'Attach product reference images first'}
          className="w-full flex items-center justify-center gap-1.5 rounded-lg bg-[#258cc0] py-1.5 text-xs font-bold text-white hover:bg-[#1a6a9e] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {shot.status === 'uploading'
            ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Queued…</>
            : shot.status === 'generating'
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Uploading & Generating…</>
              : ugcMode === 'character'
                ? <><PlayCircle className="w-3.5 h-3.5" />Generate Character Video</>
                : !hasReferences
                  ? <><ImageIcon className="w-3.5 h-3.5" />Attach reference images</>
                  : <><PlayCircle className="w-3.5 h-3.5" />Generate UGC Video</>
          }
        </button>
      </div>
    </div>
  );
}

// ─── Main NormalPanelTab ──────────────────────────────────────────────────────

export default function NormalPanelTab() {
  const {
    shots, updateShot, removeShot, selectAllShots, autoMerge, setAutoMerge,
    mpAspect, mpQuality, mpDuration, mpJobs, processingJobId,
    mpUgcMode, mpCharacterName, mpCharacterDesc, mpCharacterImageUrl, mpCharacterImageFile,
    mpEngine, autoGeneratePending, setAutoGeneratePending,
    autoGenerateJobIds, consumeAutoGenerateJob,
  } = useStore();

  const [requests, setRequests] = useState<FlowboardRequest[]>([]);
  const [loadingReqs, setLoadingReqs] = useState(false);
  const [fbHealth, setFbHealth] = useState<'ok' | 'error' | 'loading'>('loading');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [sceneStatus, setSceneStatus] = useState<'idle' | 'creating' | 'done' | 'error'>('idle');
  const [sceneError, setSceneError] = useState<string | null>(null);
  const [boardInfo, setBoardInfo] = useState<{ id: number; name: string } | null>(null);
  const [loadingBoard, setLoadingBoard] = useState(false);
  // Selected job tab (null = auto-select activeJobId)
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);

  // ── Serial generation queue (prevents concurrent uploads → socket hang up) ──
  const genQueueRef = useRef<string[]>([]);    // shot IDs waiting to be processed
  const genRunningRef = useRef(false);          // true while a shot is in-flight
  const genShotFnRef = useRef<((shot: ShotItem) => Promise<void>) | null>(null);
  const completionReportedRef = useRef<Set<string>>(new Set());
  const [genQueueLen, setGenQueueLen] = useState(0); // for display badge

  // Jobs that have shots in the store (for the tab bar)
  const jobsWithShots = mpJobs.filter(j => shots.some(s => s.jobId === j.id));
  const activeJobId = selectedJobId || processingJobId || shots[0]?.jobId;
  const activeJob = mpJobs.find(j => j.id === activeJobId);
  const mergeError = sceneError || activeJob?.fkMergeError || (activeJob?.fkMergePending && sceneStatus !== 'creating'
    ? 'Merge result unconfirmed; automatic retry paused.' : null);
  const activeUgcMode = activeJob?.ugcMode ?? mpUgcMode;
  const jobShots = activeJobId ? shots.filter(s => s.jobId === activeJobId) : shots;
  const selectedShots = jobShots.filter(s => s.selected);
  const allSelected = jobShots.length > 0 && jobShots.every(s => s.selected);

  // ── Flowboard health + request log polling ───────────────────────────────
  const fetchHealth = useCallback(async () => {
    try {
      const rpc = useStore.getState().mpEngine !== 'antigravity';
      const { data } = await api.get(rpc ? '/flowkit/health' : '/video/health');
      setFbHealth(!rpc || data?.extension_connected === true ? 'ok' : 'error');
    } catch { setFbHealth('error'); }
  }, []);

  const fetchRequests = useCallback(async () => {
    if (loadingReqs) return;
    setLoadingReqs(true);
    try {
      const rpc = useStore.getState().mpEngine !== 'antigravity';
      const r = await api.get(rpc ? '/flowkit-gen/requests' : '/video/requests?limit=20');
      const rows = Array.isArray(r.data) ? r.data : r.data?.items || [];
      const status: Record<string, string> = { COMPLETED: 'done', FAILED: 'error', PROCESSING: 'running', PENDING: 'queued' };
      setRequests(rpc ? rows.slice(0, 20).map((row: any) => ({
        id: row.id, type: row.type, created_at: row.created_at,
        status: status[row.status] || row.status,
        error: row.error_message ? extractErrorMessage(row.error_message) : undefined,
      })) : rows);
    } catch { /* Flowboard might not have a list endpoint; ignore */ }
    finally { setLoadingReqs(false); }
  }, [loadingReqs]);

  useEffect(() => {
    fetchHealth();
    fetchRequests();
    pollRef.current = setInterval(() => { fetchHealth(); fetchRequests(); }, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // ── Find/ensure a board+project for generation ───────────────────────────
  // Pass 1: use a board that already has a Flow project bound (read-only).
  // Pass 2: auto-link a Flow project to the first board (idempotent — returns
  //         the existing Google Flow project if one already exists on Google’s side).
  // Never creates a new Flowboard BOARD.
  const findExistingBoard = useCallback(async (): Promise<{ boardId: number; flowProjectId: string }> => {
    setLoadingBoard(true);
    try {
      const boardsRes = await api.get('/video/boards');
      const boards: Array<{ id: number; name: string }> = Array.isArray(boardsRes.data) ? boardsRes.data : [];
      if (boards.length === 0) throw new Error('No boards found in Flowboard. Create a board first.');
      const sorted = [...boards].reverse(); // newest first

      // Pass 1: prefer boards that already have a project bound (GET, no side-effects)
      for (const board of sorted) {
        try {
          const r = await api.get(`/video/boards/${board.id}/project`);
          const fid: string = r.data?.flow_project_id;
          if (fid) { setBoardInfo({ id: board.id, name: board.name }); return { boardId: board.id, flowProjectId: fid }; }
        } catch { /* no binding — try next */ }
      }

      // Pass 2: ensure a project for the newest board (POST is idempotent —
      // links a Google Flow project only if none is bound yet)
      for (const board of sorted) {
        try {
          const r = await api.post(`/video/boards/${board.id}/project`, {});
          const fid: string = r.data?.flow_project_id;
          if (fid) { setBoardInfo({ id: board.id, name: board.name }); return { boardId: board.id, flowProjectId: fid }; }
        } catch { /* couldn’t link — try next board */ }
      }

      throw new Error('Could not link a Flow project. Make sure Google Flow is open and the Flowboard extension is connected.');
    } finally {
      setLoadingBoard(false);
    }
  }, []);
  useEffect(() => {
    if (!autoGeneratePending) return;
    setAutoGeneratePending(false);

    // Small delay to let the component/shots fully render first, then enqueue all
    const t = setTimeout(() => {
      // Only enqueue shots that haven't started yet — avoids re-triggering
      // already-generating shots when a 2nd job arrives with autoProcess:true
      const readyShots = useStore.getState().shots.filter(s => s.selected && s.status === 'pending');
      if (readyShots.length > 0) {
        // Use enqueueRef so we always call the latest enqueueShots, avoiding stale closure
        enqueueRef.current(readyShots);
      }
    }, 600);
    return () => clearTimeout(t);
  }, [autoGeneratePending, setAutoGeneratePending]);

  // ── Generate one shot (UGC: upload images → gen_video_omni) ─────────────
  // How long to wait between polls after dispatching gen_video_omni.
  // Flowboard's worker polls every 10s internally; we poll a little slower.
  const VIDEO_GEN_POLL_MS  = 15_000;  // 15 s
  const VIDEO_GEN_MAX_POLLS = 40;      // 40 × 15 s = 10 min hard cap
  const waitForRequest = async (requestId: number): Promise<string> => {
    for (let i = 0; i < VIDEO_GEN_MAX_POLLS; i++) {
      await new Promise(r => setTimeout(r, VIDEO_GEN_POLL_MS));
      try {
        const r = await api.get(`/video/requests/${requestId}`);
        const s: string = r.data?.status || '';
        if (['done', 'failed', 'error', 'timeout', 'canceled'].includes(s)) return s;
      } catch { /* network hiccup — keep polling */ }
    }
    return 'timeout';
  };

  const reportJobCompletion = async (jobId: string) => {
    if (completionReportedRef.current.has(jobId)) return;
    const jobShots = useStore.getState().shots.filter(shot => shot.jobId === jobId);
    if (!jobShots.length || jobShots.some(shot => shot.status === 'pending' || shot.status === 'uploading' || shot.status === 'ready' || shot.status === 'generating')) return;

    completionReportedRef.current.add(jobId);
    const completedShots = jobShots.filter(shot => shot.status === 'done');
    const failedShots = jobShots.filter(shot => shot.status === 'error');
    try {
      await api.post('/n8n/studio/shot-complete', {
        jobId,
        totalShots: jobShots.length,
        doneShots: completedShots.length,
        failedShots: failedShots.length,
        videoUrls: completedShots.map(shot => shot.videoUrl).filter((url): url is string => Boolean(url)),
        failedShotDetails: failedShots.map(shot => ({
          shotNumber: shot.shotNumber,
          reason: shot.errorMsg || 'Flowkit generation failed without a returned error message.',
        })),
      });
    } catch {
      completionReportedRef.current.delete(jobId);
    }
  };

  useEffect(() => {
    const reportTerminalUgcJobs = async () => {
      for (const job of useStore.getState().mpJobs) {
        if (job.id.startsWith('ugc-')) await reportJobCompletion(job.id);
      }
    };
    void reportTerminalUgcJobs();
    const retryTimer = setInterval(() => { void reportTerminalUgcJobs(); }, 10_000);
    return () => clearInterval(retryTimer);
  }, [mpJobs, shots]);

  const generateShotFlowboard = async (shot: ShotItem) => {
    updateShot(shot.id, { status: 'generating', errorMsg: undefined });
    // Use getState() to avoid stale closure when called from the queue drainer
    const { mpJobs: freshJobs, mpAspect: freshAspect, updateMpJob: freshUpdateJob } = useStore.getState();
    const job = freshJobs.find(j => j.id === shot.jobId);
    const aspect = job?.aspect || freshAspect;

    try {
      // ── 1. Find or reuse the shared board+project (NEVER create new) ──────────────
      // All shots of the same job share one board; the board must already
      // have a Google Flow project linked in Flowboard.
      let boardId: number;
      let flowProjectId: string;

      const latestJob = useStore.getState().mpJobs.find(j => j.id === shot.jobId);
      if (latestJob?.boardId && latestJob?.flowProjectId) {
        boardId = latestJob.boardId;
        flowProjectId = latestJob.flowProjectId;
      } else {
        // First shot of this job: discover the existing board+project
        const found = await findExistingBoard();
        boardId = found.boardId;
        flowProjectId = found.flowProjectId;
        freshUpdateJob(shot.jobId, { boardId, flowProjectId, sceneId: crypto.randomUUID() });
      }

      // Helper: board was deleted/reset — find another valid one
      const refindBoard = async (): Promise<{ boardId: number; flowProjectId: string }> => {
        const found = await findExistingBoard();
        useStore.getState().updateMpJob(shot.jobId, {
          boardId: found.boardId, flowProjectId: found.flowProjectId, sceneId: crypto.randomUUID(),
          // Clear shared VA data when board changes so they get recreated
          vaNodeIds: undefined, vaMediaIds: undefined,
        });
        return found;
      };

      // ── 2a. Delete stale VIDEO node only (VA nodes are job-shared, keep them) ─
      const staleVideoNodeId = useStore.getState().shots.find(s => s.id === shot.id)?.nodeIds?.video;
      if (typeof staleVideoNodeId === 'number') {
        await api.delete(`/video/nodes/${staleVideoNodeId}`).catch(() => {});
        updateShot(shot.id, { nodeIds: undefined });
      }

      // ── 2b. Check for job-shared VA nodes (reused across all shots) ─────
      const jobForVa = useStore.getState().mpJobs.find(j => j.id === shot.jobId);
      const sharedVaNodeIds: number[] | undefined = jobForVa?.vaNodeIds;
      const sharedVaMediaIds: string[] | undefined = jobForVa?.vaMediaIds;

      // Safety check: if image count changed since VA nodes were created, invalidate and recreate
      const imageCountMismatch =
        sharedVaMediaIds?.length !== undefined &&
        sharedVaMediaIds.length > 0 &&
        sharedVaMediaIds.length !== shot.imageDataUrls.length;
      if (imageCountMismatch) {
        useStore.getState().updateMpJob(shot.jobId, { vaNodeIds: undefined, vaMediaIds: undefined });
      }
      const reusingVa = !imageCountMismatch && !!(sharedVaNodeIds?.length && sharedVaMediaIds?.length);

      // ── 2c. Clean stale nodes from previous sessions (runs once per job's fresh start) ──
      if (!reusingVa) {
        try {
          const boardData = await api.get(`/video/boards/${boardId}`);
          const allBoardNodes: Array<{ id: number; type: string }> = boardData.data?.nodes || [];
          // Collect node IDs currently tracked by any active job/shot in this store session
          const { mpJobs: aJobs, shots: aShots } = useStore.getState();
          const activeIds = new Set<number>([
            ...aJobs.flatMap(j => j.vaNodeIds || []),
            ...aShots.map(s => s.nodeIds?.video).filter((id): id is number => id !== undefined),
          ]);
          const stale = allBoardNodes.filter(
            n => ['visual_asset', 'video', 'scene'].includes(n.type) && !activeIds.has(n.id)
          );
          if (stale.length > 0) {
            await Promise.allSettled(stale.map(n => api.delete(`/video/nodes/${n.id}`).catch(() => {})));
          }
        } catch { /* non-fatal */ }
      }

      // ── 2. Scaffold shot nodes on the SHARED board ──────────────────────
      // Pass existingVaNodeIds to skip VA creation for shots after the first.
      // Retry once if the board was deleted/reset (Flowboard DB reset returns 404)
      // eslint-disable-next-line prefer-const
      const characterJob = useStore.getState().mpJobs.find(j => j.id === shot.jobId);
      const isCharacterMode = characterJob?.ugcMode === 'character';
      const charImageUrl   = characterJob?.characterImageUrl || '';
      const charImageFile  = characterJob?.characterImageFile || '';
      const charName       = characterJob?.characterName || '';
      const charDesc       = characterJob?.characterDesc || '';
      const charContract   = isCharacterMode && charName
        ? characterContinuityPrompt(charName, charDesc, characterJob?.characterProfile)
        : '';
      let scaffoldData: { boardId: number; nodeIds: { visualAssets: number[]; prompt?: number; character?: number; video: number } };
      const scaffoldBody = () => ({
        boardName: `${job?.idea?.slice(0, 40) || 'Master Prompt'} — ${aspect}`,
        shotNumber: shot.shotNumber,
        prompt: shot.prompt,
        material: job?.material || 'Live Action',
        aspect,
        boardId,
        refCount: Math.max(2, shot.imageDataUrls.length),
        referenceNames: shot.imageFiles,
        // Pass shared VA node IDs to skip re-creation (undefined = create new)
        existingVaNodeIds: reusingVa ? sharedVaNodeIds : undefined,
        character: isCharacterMode && charName ? {
          name: charName,
          description: charContract,
          imageUrl: charImageUrl || undefined,
          imageFile: charImageFile || undefined,
          profile: characterJob?.characterProfile,
        } : undefined,
      });
      try {
        scaffoldData = (await api.post('/script/scaffold-shot', scaffoldBody())).data;
      } catch (scaffoldErr: any) {
        const msg: string = scaffoldErr.response?.data?.detail || scaffoldErr.response?.data?.error || scaffoldErr.message || '';
        if (msg.includes('404') || msg.toLowerCase().includes('board')) {
          const fresh = await refindBoard();
          boardId = fresh.boardId;
          flowProjectId = fresh.flowProjectId;
          scaffoldData = (await api.post('/script/scaffold-shot', { ...scaffoldBody(), boardId, existingVaNodeIds: undefined })).data;
        } else {
          throw scaffoldErr;
        }
      }
      const { nodeIds } = scaffoldData;
      updateShot(shot.id, { boardId, nodeIds });

      let charMediaId: string | null = null;

      if (isCharacterMode && charImageUrl) {
        // Reuse already-uploaded media_id for this job (upload once, reuse everywhere)
        const existingCharMediaId = useStore.getState().mpJobs.find(j => j.id === shot.jobId)?.charMediaId;
        if (existingCharMediaId) {
          charMediaId = existingCharMediaId;
        } else {
          // First shot: upload the character reference image
          const charRes = await fetch(charImageUrl);
          const charBlob = await charRes.blob();
          const charFile = new File([charBlob], charImageFile || 'character-ref.jpg', { type: charBlob.type || 'image/jpeg' });
          const charForm = new FormData();
          charForm.append('project_id', flowProjectId);
          charForm.append('file', charFile);
          // node_id is optional — character images don't need a VA node
          const charUploadRes = await fetch('/api/upload', {
            method: 'POST',
            headers: { Authorization: `Bearer ${useStore.getState().token || ''}` },
            body: charForm,
          });
          if (charUploadRes.ok) {
            ({ media_id: charMediaId } = await charUploadRes.json() as { media_id: string });
            useStore.getState().updateMpJob(shot.jobId, { charMediaId: charMediaId! });
          }
          // Non-fatal: retain the job's text-only character profile if the optional reference upload fails.
        }
      }
      if (charMediaId && nodeIds.character) {
        try {
          await api.patch(`/video/nodes/${nodeIds.character}`, { data: { mediaId: charMediaId, prompt: charContract, status: 'done' } });
        } catch { /* The character node still preserves the profile and reference URL. */ }
      }

      // ── 3. Upload images → get media_ids (skip if already uploaded for this job) ─
      let mediaIds: string[];
      if (reusingVa && sharedVaMediaIds!.length > 0) {
        // Reuse the media_ids uploaded for the first shot — no re-upload needed
        mediaIds = sharedVaMediaIds!;
      } else {
        // First shot: upload images and store media_ids + VA node IDs on the job
        mediaIds = [];
        for (let i = 0; i < shot.imageDataUrls.length; i++) {
          const dataUrl = shot.imageDataUrls[i];
          const filename = shot.imageFiles[i] || `shot-${shot.shotNumber}-ref-${i + 1}.jpg`;

          const res = await fetch(dataUrl);
          const blob = await res.blob();
          const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
          const vaNodeId = nodeIds.visualAssets[i] ?? nodeIds.visualAssets[nodeIds.visualAssets.length - 1];

          // Retry up to 3 times on transient errors (socket hang up, 502+)
          let media_id: string | null = null;
          for (let attempt = 0; attempt < 3; attempt++) {
            if (attempt > 0) await new Promise(r => setTimeout(r, 2000 * attempt));
            const form = new FormData();
            form.append('project_id', flowProjectId);
            form.append('file', file);
            form.append('node_id', String(vaNodeId));
            const uploadRes = await fetch('/api/upload', {
              method: 'POST',
              headers: { Authorization: `Bearer ${useStore.getState().token || ''}` },
              body: form,
            });
            if (uploadRes.ok) {
              ({ media_id } = await uploadRes.json() as { media_id: string });
              break;
            }
            const errText = await uploadRes.text();
            const parsedErr = (() => { try { return JSON.parse(errText)?.error || ''; } catch { return errText; } })();
            // Non-transient error (not 5xx, not socket-related) → fail fast
            if (!parsedErr.includes('socket') && !parsedErr.includes('hang') && uploadRes.status < 500) {
              throw new Error(`Upload failed: ${errText}`);
            }
            // 5xx or socket error → retry (fall through to next attempt)
            if (attempt === 2) throw new Error(`Upload failed after 3 attempts: ${errText}`);
          }
          if (!media_id) throw new Error('Upload returned no media_id');
        mediaIds.push(media_id);

        try { await api.patch(`/video/nodes/${vaNodeId}`, { data: { mediaId: media_id, status: 'done' } }); } catch { /* non-fatal */ }
      }
        // Store VA node IDs and media IDs on the job so subsequent shots reuse them
        useStore.getState().updateMpJob(shot.jobId, {
          vaNodeIds: nodeIds.visualAssets,
          vaMediaIds: mediaIds,
        });
      } // end if/else reusingVa

      // ── 4. Dispatch one direct video for the approved shot ─────────────
      let paygateTier = 'PAYGATE_TIER_ONE';
      try {
        const authRes = await api.get('/api/auth/me');
        paygateTier = authRes.data?.paygate_tier || 'PAYGATE_TIER_ONE';
      } catch {}

      // Map UI aspect ratio to Flow's VIDEO enum
      const VIDEO_AR: Record<string, string> = {
        '9:16': 'VIDEO_ASPECT_RATIO_PORTRAIT',
        '16:9': 'VIDEO_ASPECT_RATIO_LANDSCAPE',
        '1:1':  'VIDEO_ASPECT_RATIO_PORTRAIT',
        '4:3':  'VIDEO_ASPECT_RATIO_LANDSCAPE',
      };
      const allowedDurations = [4, 6, 8, 10];
      const durationS = allowedDurations.reduce((nearest, candidate) =>
        Math.abs(candidate - shot.durationSec) < Math.abs(nearest - shot.durationSec) ? candidate : nearest, 8
      );

      // Faceless UGC keeps the established direct path: the complete shot
      // prompt drives motion and the original product images are references.
      // Character jobs continue below with ordered beat-image sequences.
      if (!isCharacterMode) {
        const directPrompt = normalPanelGenerationPrompt(shot);
        await api.patch(`/video/nodes/${nodeIds.video}`, {
          data: {
            prompt: directPrompt,
            visualAction: shot.prompt.split(/\bGoogle Flow:/i)[0].trim(),
            approvedVoiceover: shot.voiceover,
          },
        });
        const directVideoRequest = await api.post('/video/requests', {
          node_id: nodeIds.video,
          type: 'gen_video_omni',
          params: {
            prompt: directPrompt,
            project_id: flowProjectId,
            ref_media_ids: mediaIds,
            aspect_ratio: VIDEO_AR[aspect] || 'VIDEO_ASPECT_RATIO_PORTRAIT',
            paygate_tier: paygateTier,
            duration_s: durationS,
            creative_mode: 'ugc',
            approved_voiceover: shot.voiceover,
            ugc_studio_job_id: shot.jobId,
            scene_id: useStore.getState().mpJobs.find(j => j.id === shot.jobId)?.sceneId,
          },
        });
        const requestId: number = directVideoRequest.data.id;
        updateShot(shot.id, { requestId, status: 'generating', errorMsg: undefined });
        fetchRequests();
        const finalStatus = await waitForRequest(requestId);
        if (finalStatus === 'done') {
          const videoResult = await api.get(`/video/requests/${requestId}`);
          const videoMediaId = videoResult.data?.result?.media_ids?.[0] as string | undefined;
          await api.patch(`/video/nodes/${nodeIds.video}`, { data: { mediaId: videoMediaId, status: 'done' } });
          updateShot(shot.id, { status: 'done', videoUrl: videoMediaId ? `/media/${videoMediaId}` : undefined });
        } else {
          updateShot(shot.id, {
            status: 'error',
            errorMsg: `Generation ${finalStatus} — see Flowboard Activity for details`,
          });
        }
        await reportJobCompletion(shot.jobId);
        fetchRequests();
        return;
      }
      const effectivePrompt = [charContract || null, shot.prompt].filter(Boolean).join('\n\n');
      const effectiveRefIds = charMediaId ? [charMediaId, ...mediaIds] : mediaIds;
      const videoRequest = await api.post('/video/requests', {
        node_id: nodeIds.video,
        type: 'gen_video_omni',
        params: {
          prompt: effectivePrompt,
          project_id: flowProjectId,
          ref_media_ids: effectiveRefIds,
          aspect_ratio: VIDEO_AR[aspect] || 'VIDEO_ASPECT_RATIO_PORTRAIT',
          paygate_tier: paygateTier,
          duration_s: durationS,
          creative_mode: 'ugc',
          ugc_studio_job_id: shot.jobId,
          scene_id: useStore.getState().mpJobs.find(j => j.id === shot.jobId)?.sceneId,
        },
      });
      const requestId: number = videoRequest.data.id;
      updateShot(shot.id, { requestId, status: 'generating', errorMsg: undefined });
      fetchRequests();
      const finalStatus = await waitForRequest(requestId);
      if (finalStatus === 'done') {
        const videoResult = await api.get(`/video/requests/${requestId}`);
        const videoMediaId = videoResult.data?.result?.media_ids?.[0] as string | undefined;
        await api.patch(`/video/nodes/${nodeIds.video}`, { data: { mediaId: videoMediaId, status: 'done' } });
        updateShot(shot.id, { status: 'done', videoUrl: videoMediaId ? `/media/${videoMediaId}` : undefined });
      } else {
        updateShot(shot.id, {
          status: 'error',
          errorMsg: `Generation ${finalStatus} — see Flowboard Activity for details`,
        });
      }
      await reportJobCompletion(shot.jobId);
      fetchRequests();
    } catch (e: any) {
      // FastAPI errors use {detail:...}, Express errors use {error:...}
      const errMsg = e.response?.data?.detail || e.response?.data?.error || e.message;
      updateShot(shot.id, { status: 'error', errorMsg: errMsg });
      await reportJobCompletion(shot.jobId);
    }
  };

  // Both Studio engines keep their orchestration routes, but submit native
  // Omni reference video through the same signed RPC bridge (never a Veo still fallback).
  const generateShotRpc = async (shot: ShotItem, engine: RpcEngine) => {
    updateShot(shot.id, { status: 'generating', errorMsg: undefined });
    const { mpJobs, mpAspect } = useStore.getState();
    const job = mpJobs.find(item => item.id === shot.jobId);
    const orientation = FK_ORIENTATION[job?.aspect || mpAspect] || 'VERTICAL';
    try {
      const durationSec = nearestOmniDuration(shot.durationSec);
      updateShot(shot.id, { durationSec });
      const videoUrl = await generateReferenceVideo({
        api, engine, shot: { ...shot, durationSec }, orientation,
        prompt: normalPanelGenerationPrompt({ ...shot, durationSec }),
        characterPrompt: job?.ugcMode === 'character' && !job.characterUgc && job.characterName
          ? characterContinuityPrompt(job.characterName, job.characterDesc || '', job.characterProfile) : undefined,
        getJob: () => {
          const current = useStore.getState().mpJobs.find(item => item.id === shot.jobId);
          if (!current) throw new Error('This job was removed while generating');
          return current;
        },
        updateJob: patch => useStore.getState().updateMpJob(shot.jobId, patch),
        updateShot: patch => useStore.getState().updateShot(shot.id, patch),
      });
      updateShot(shot.id, { status: 'done', videoUrl });
    } catch (cause) {
      updateShot(shot.id, { status: 'error', errorMsg: extractErrorMessage(cause) });
    } finally {
      await reportJobCompletion(shot.jobId);
    }
  };

  // ── Engine dispatcher — routes to the engine chosen in Master Prompt ────
  // A new shot uses the current selection. Accepted/uncertain work retains
  // its original route even after reload or a later selection change.
  const generateShot = async (shot: ShotItem) => {
    const engine = generationEngine(shot, useStore.getState().mpEngine);
    const job = useStore.getState().mpJobs.find(item => item.id === shot.jobId);
    if ((job?.characterUgc || shot.characterUgcVersion) && engine === 'antigravity') {
      updateShot(shot.id, { status: 'error', errorMsg: CHARACTER_UGC_ENGINE_ERROR });
      return;
    }
    updateShot(shot.id, { engine });
    if (engine === 'flowkit' || engine === 'flowboard') return generateShotRpc(shot, engine);
    return generateShotFlowboard(shot);
  };

  // Keep ref in sync so drainQueue always calls the latest generateShot
  genShotFnRef.current = generateShot;

  // ── Queue drainer: runs shots serially, one at a time ────────────────────
  const drainQueue = useCallback(async () => {
    if (genRunningRef.current) return;
    genRunningRef.current = true;

    while (genQueueRef.current.length > 0) {
      setGenQueueLen(genQueueRef.current.length);
      const shotId = genQueueRef.current[0];
      // Get a fresh shot from store — it may have been updated since enqueue
      const freshShot = useStore.getState().shots.find(s => s.id === shotId);
      const state = useStore.getState();
      const job = state.mpJobs.find(item => item.id === freshShot?.jobId);
      const engine = freshShot && (job?.characterUgc || freshShot.characterUgcVersion)
        ? generationEngine(freshShot, state.mpEngine) : state.mpEngine;
      const hasInputs = freshShot && (engine === 'antigravity'
        ? freshShot.imageDataUrls.length > 0 : hasReferenceInput(freshShot, job, engine));
      // Skip only if CURRENTLY generating (status set by generateShot itself),
      // or if images were removed. Allow 'done'/'error' to be re-generated.
      if (freshShot && freshShot.status !== 'generating' && hasInputs && genShotFnRef.current) {
        await genShotFnRef.current(freshShot);
      } else if (freshShot && !hasInputs) {
        // Images were removed — reset to pending so the user can re-add them
        useStore.getState().updateShot(freshShot.id, { status: 'pending', errorMsg: 'Images removed while queued — please re-add and generate again.' });
      }
      genQueueRef.current = genQueueRef.current.slice(1);
    }

    genRunningRef.current = false;
    setGenQueueLen(0);
  }, []);

  // ── Enqueue shots for serial generation ──────────────────────────────────
  const enqueueShots = useCallback((toGenerate: ShotItem[]) => {
    const state = useStore.getState();
    const eligible = toGenerate.filter(shot => {
      if (shot.status === 'generating' || shot.status === 'uploading' || genQueueRef.current.includes(shot.id)) return false;
      const job = state.mpJobs.find(item => item.id === shot.jobId);
      const characterUgc = job?.characterUgc || shot.characterUgcVersion;
      const engine = characterUgc && shot.status !== 'done' ? generationEngine(shot, state.mpEngine) : state.mpEngine;
      if (characterUgc && engine === 'antigravity') {
        updateShot(shot.id, { errorMsg: CHARACTER_UGC_ENGINE_ERROR });
        return false;
      }
      return engine === 'antigravity' ? shot.imageDataUrls.length > 0 : hasReferenceInput(shot, job, engine);
    });
    if (!eligible.length) return;

    // Mark as uploading so UI shows they're queued
    eligible.forEach(s => {
      completionReportedRef.current.delete(s.jobId);
      updateShot(s.id, {
        status: 'uploading', errorMsg: undefined,
        // An explicit Generate on a finished shot starts a new render; an
        // interrupted/failed shot retains its request handle for safe resume.
        ...(s.status === 'done' ? { fkVideoRequestId: undefined, fkSceneId: undefined, rpcSubmissionPending: false } : {}),
      });
    });
    genQueueRef.current = [...genQueueRef.current, ...eligible.map(s => s.id)];
    setGenQueueLen(genQueueRef.current.length);
    drainQueue();
  }, [drainQueue, updateShot]);

  // Stable ref so the Auto Mode effect (with [] deps) can call the latest enqueueShots
  const enqueueRef = useRef(enqueueShots);
  enqueueRef.current = enqueueShots;

  useEffect(() => {
    const jobId = autoGenerateJobIds[0];
    if (!jobId) return;
    const jobExists = useStore.getState().mpJobs.some(job => job.id === jobId);
    const jobShots = autoGenerationShots(jobId, useStore.getState().shots);
    if (!jobExists) {
      consumeAutoGenerateJob();
      return;
    }
    if (!jobShots.length) return;
    enqueueRef.current(jobShots);
    consumeAutoGenerateJob();
  }, [autoGenerateJobIds, mpJobs, shots, consumeAutoGenerateJob]);

  // Generate selected: enqueue instead of firing all at once
  const generateSelected = () => enqueueShots(selectedShots);

  // ── Native merge: existing ordered clips, rendered by OpenCut via Studio ──
  const handleAutoMergeFlowkit = async (jobId: string, doneShots: ShotItem[]) => {
    const job = useStore.getState().mpJobs.find(j => j.id === jobId);
    if (!job) return;

    setSceneStatus('creating');
    setSceneError(null);
    useStore.getState().updateMpJob(jobId, { fkMergePending: true, fkMergeError: undefined });
    try {
      const mergedUrl = await mergeReferenceShots(api, job, doneShots);
      useStore.getState().updateMpJob(jobId, { fkMergedVideoUrl: mergedUrl, fkMergePending: false, fkMergeError: undefined });
      setSceneStatus('done');
    } catch (e: any) {
      const msg = extractErrorMessage(e, 'Video merge failed');
      useStore.getState().updateMpJob(jobId, { fkMergePending: false, fkMergeError: msg });
      setSceneError(msg);
      setSceneStatus('error');
    }
  };

  // Auto and manual merge validate the same complete selected set. Failed or
  // legacy clips must not disappear into separate partial merges. Existing
  // saved outputs/legacy requests are retained; never requeue them on remount.
  const mergeReportedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const checkAutoMerge = async () => {
      const { autoMerge: autoMergeOn, mpJobs: jobs, shots: allShots } = useStore.getState();
      for (const job of jobs) {
        if (!(job.autoMerge ?? autoMergeOn)) continue;
        if (mergeReportedRef.current.has(job.id)) continue;
        if (job.fkMergedVideoUrl || job.sceneRequestId || job.fkMergePending || job.fkMergeError) continue;
        const jShots = allShots.filter(s => s.jobId === job.id && s.selected);
        if (!jShots.length) continue;
        const allTerminal = jShots.every(s => s.status === 'done' || s.status === 'error');
        if (!allTerminal) continue;
        mergeReportedRef.current.add(job.id);
        await handleAutoMergeFlowkit(job.id, jShots);
      }
    };
    void checkAutoMerge();
    const t = setInterval(() => void checkAutoMerge(), 5000);
    return () => clearInterval(t);
  }, []);

  const statusDot = fbHealth === 'ok' ? 'bg-[#76a441]' : fbHealth === 'error' ? 'bg-[#dc553c]' : 'bg-[#c8942e]';

  return (
    <div className="h-full flex flex-col bg-[#f4f4f6] overflow-hidden">
      {/* ── Top toolbar ── */}
      <div className="bg-white border-b border-gray-200 px-4 py-2 shrink-0">
        <div className="flex items-center gap-3 flex-wrap">
          {/* Config pills */}
          <div className="flex items-center gap-2 text-xs">
            <span className="font-bold text-gray-500">Aspect</span>
            <span className="rounded bg-[#258cc0] text-white px-2 py-0.5 font-bold">{mpAspect}</span>
            <span className="font-bold text-gray-500 ml-2">Quality</span>
            <span className="rounded bg-[#865916]/20 text-[#865916] px-2 py-0.5 font-bold">{mpQuality}</span>
            <span className="font-bold text-gray-500 ml-2">Duration</span>
            <span className="rounded bg-gray-100 text-gray-700 px-2 py-0.5 font-bold">{mpDuration}s</span>
            <span className="font-bold text-gray-500 ml-2">UGC</span>
            <span className={`rounded px-2 py-0.5 font-bold ${activeUgcMode === 'character' ? 'bg-[#865916] text-white' : 'bg-gray-100 text-gray-700'}`}>
              {activeUgcMode === 'character' ? '👤 Character' : '🎬 Faceless'}
            </span>
          </div>

          {/* Board+project indicator */}
          {boardInfo && (
            <span className="flex items-center gap-1 text-xs text-[#865916] font-bold truncate max-w-[140px]" title={`Board: ${boardInfo.name} (id ${boardInfo.id})`}>
              <ExternalLink className="w-3 h-3 shrink-0" />
              {boardInfo.name.slice(0, 22)}{boardInfo.name.length > 22 ? '…' : ''}
            </span>
          )}
          {loadingBoard && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#865916]" />}

          <div className="flex-1" />

          {/* Flowboard status */}
          <span className="flex items-center gap-1.5 text-xs font-bold text-gray-500">
            <span className={`w-2 h-2 rounded-full ${statusDot}`} />
            {mpEngine === 'antigravity' ? 'Flowboard' : 'Flow RPC'} {fbHealth === 'ok' ? 'connected' : fbHealth === 'error' ? 'offline' : '…'}
          </span>
          <button onClick={() => { fetchHealth(); fetchRequests(); }} className="p-1.5 rounded hover:bg-gray-100">
            <RefreshCw className="w-3.5 h-3.5 text-gray-500" />
          </button>
        </div>

        {/* Action toolbar */}
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <button
            onClick={() => selectAllShots(!allSelected)}
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-bold text-gray-600 hover:bg-gray-50 transition-colors"
          >
            {allSelected ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
            {allSelected ? 'Đã Chọn Tất Cả' : 'Chọn Tất Cả'}
            {selectedShots.length > 0 && <span className="ml-0.5 text-[#865916]">({selectedShots.length}/{jobShots.length})</span>}
          </button>

          <button
            onClick={() => {
              if (!activeJobId) return;
              void handleAutoMergeFlowkit(activeJobId, selectedShots);
            }}
            disabled={sceneStatus === 'creating' || !selectedShots.length}
            title="Merge every selected shot, in shot order, using its saved native scene"
            className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-bold text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors"
          >
            {sceneStatus === 'creating'
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Creating Scene…</>
              : sceneStatus === 'done'
                ? <><Merge className="w-3.5 h-3.5 text-[#76a441]" />Scene Created ✓</>
                : sceneStatus === 'error'
                  ? <><Merge className="w-3.5 h-3.5 text-[#dc553c]" />Scene Failed</>
                  : <><Merge className="w-3.5 h-3.5" />Create Scene + Merge</>
            }
          </button>
          {mergeError && (
            <span role="alert" className="text-[10px] text-[#8c2d1f] truncate max-w-[240px]" title={mergeError}>{mergeError}</span>
          )}
          {activeJob?.fkMergedVideoUrl && (
            <a href={activeJob.fkMergedVideoUrl} target="_blank" rel="noopener noreferrer"
              className="text-xs font-bold text-[#476b24] underline">Open merged video</a>
          )}

          <label className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-bold text-gray-600 cursor-pointer">
            <input type="checkbox" checked={activeJob?.autoMerge ?? autoMerge} onChange={e => {
              setAutoMerge(e.target.checked);
              if (activeJob) useStore.getState().updateMpJob(activeJob.id, { autoMerge: e.target.checked });
            }} className="accent-[#865916]" />
            <LayoutGrid className="w-3.5 h-3.5" />Auto Merge
          </label>

          <div className="flex-1" />

          {/* Progress summary */}
          {jobShots.length > 0 && (
            <span className="text-[10px] font-bold text-gray-500 flex items-center gap-1.5">
              {jobShots.filter(s => s.status === 'done').length > 0 &&
                <span className="text-[#476b24]">✓{jobShots.filter(s => s.status === 'done').length}</span>}
              {jobShots.filter(s => s.status === 'error').length > 0 &&
                <span className="text-[#dc553c]">✗{jobShots.filter(s => s.status === 'error').length}</span>}
              <span className="text-gray-400">/{jobShots.length}</span>
            </span>
          )}

          <button
            onClick={generateSelected}
            disabled={selectedShots.filter(s => s.status !== 'generating' && s.status !== 'uploading').length === 0}
            className="flex items-center gap-2 rounded-lg bg-[#258cc0] px-4 py-1.5 text-xs font-black text-white hover:bg-[#1a6a9e] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {genQueueLen > 0
              ? <><Loader2 className="w-4 h-4 animate-spin" />Generating {genQueueLen} left…</>
              : (() => {
                  const canRedo = selectedShots.filter(s => s.status !== 'generating' && s.status !== 'uploading');
                  const hasDone = canRedo.some(s => s.status === 'done');
                  const hasError = canRedo.some(s => s.status === 'error');
                  const fresh = canRedo.filter(s => s.status !== 'done' && s.status !== 'error').length;
                  if (hasDone || hasError) return <><RefreshCw className="w-4 h-4" />Re-generate ({canRedo.length})</>;
                  return <><PlayCircle className="w-4 h-4" />Tạo Video ({fresh || canRedo.length})</>;
                })()
            }
          </button>
        </div>
      </div>

      {/* ── Main split: cards LEFT | result log RIGHT ── */}
      <div className="flex-1 flex overflow-hidden">

        {/* Left: Shot prompt cards grid */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Job selector tabs (shown when >1 job has shots) */}
          {jobsWithShots.length > 1 && (
            <div className="flex items-center gap-1.5 px-4 py-2 bg-white border-b border-gray-100 overflow-x-auto shrink-0">
              <span className="text-[10px] font-bold text-gray-400 shrink-0 mr-1">UGC:</span>
              {jobsWithShots.map(j => {
                const jShots = shots.filter(s => s.jobId === j.id);
                const done = jShots.filter(s => s.status === 'done').length;
                const isActive = activeJobId === j.id;
                const imgCount = j.jobImageUrls?.length || j.fkVaMediaIds?.length || j.batchRpcReferenceMediaIds?.length || jShots[0]?.imageDataUrls?.length || 0;
                return (
                  <button key={j.id} onClick={() => setSelectedJobId(j.id)}
                    className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[11px] font-bold transition-colors shrink-0 ${
                      isActive ? 'bg-[#865916] text-white' : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {j.idea.slice(0, 18)}{j.idea.length > 18 ? '…' : ''}
                    <span className={`rounded-full px-1 text-[9px] ${isActive ? 'bg-white/25' : 'bg-gray-100 text-gray-500'}`}>
                      {done}/{jShots.length}
                    </span>
                    {imgCount > 0 && (
                      <span className={`rounded-full px-1 text-[9px] flex items-center gap-0.5 ${isActive ? 'bg-white/20' : 'bg-[#e9d6aa] text-[#6d5b3f]'}`}>
                        <ImageIcon className="w-2.5 h-2.5" />{imgCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          <div className="flex-1 overflow-y-auto p-4">
          {jobShots.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <LayoutGrid className="w-12 h-12 text-gray-300 mb-3" />
              <p className="text-sm font-bold text-gray-400">No shots yet</p>
              <p className="text-xs text-gray-400 mt-1">
                Go to <strong>Master Prompt</strong> → generate a script → click <strong>Start Processing</strong>
              </p>
              <button
                onClick={() => useStore.getState().setActiveTab('masterPrompt')}
                className="mt-4 flex items-center gap-1.5 rounded-lg bg-[#865916] px-4 py-2 text-xs font-bold text-white hover:bg-[#6d4712] transition-colors"
              >
                <ChevronRight className="w-3.5 h-3.5" />Go to Master Prompt
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {jobShots.map((shot, i) => (
                <ShotCard
                  key={shot.id}
                  shot={shot}
                  index={i}
                  onUpdate={(id, patch) => updateShot(id, patch)}
                  onRemove={removeShot}
                  onGenerate={(shot) => enqueueShots([shot])}
                  ugcMode={activeUgcMode}
                />
              ))}
            </div>
          )}
          </div>{/* end scroll area */}
        </div>{/* end left column */}

        {/* Right: Result log (Flowboard requests) */}
        <div className="w-64 shrink-0 bg-white border-l border-gray-200 flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 shrink-0">
            <span className="text-xs font-black tracking-wide text-gray-500 uppercase">Result Log</span>
            <button onClick={fetchRequests} className="p-1 rounded hover:bg-gray-100">
              <RefreshCw className={`w-3 h-3 text-gray-400 ${loadingReqs ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 border-b border-gray-100 shrink-0">
            {[
              { label: 'TOTAL',   val: requests.length,                             color: 'text-gray-700' },
              { label: 'SUCCESS', val: requests.filter(r => r.status === 'done').length,  color: 'text-green-600' },
              { label: 'FAILED',  val: requests.filter(r => r.status === 'error').length, color: 'text-red-500' },
            ].map(({ label, val, color }) => (
              <div key={label} className="flex flex-col items-center py-2 border-r last:border-r-0 border-gray-100">
                <span className={`text-base font-black ${color}`}>{val}</span>
                <span className="text-[9px] font-bold text-gray-400">{label}</span>
              </div>
            ))}
          </div>

          {/* Request list */}
          <div className="flex-1 overflow-y-auto">
            {requests.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full py-8 text-center">
                <p className="text-xs text-gray-400">No requests yet</p>
              </div>
            ) : (
              <table className="w-full text-[10px]">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="px-2 py-1.5 text-left font-bold text-gray-500">ID</th>
                    <th className="px-2 py-1.5 text-left font-bold text-gray-500">TYPE</th>
                    <th className="px-2 py-1.5 text-left font-bold text-gray-500">STATUS</th>
                    <th className="px-2 py-1.5 text-left font-bold text-gray-500">ERROR</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.slice(0, 50).map(req => (
                    <tr key={req.id} className="border-b border-gray-50 hover:bg-gray-50">
                      <td className="px-2 py-1.5 text-gray-500" title={String(req.id)}>{typeof req.id === 'string' ? req.id.slice(0, 8) : req.id}</td>
                      <td className="px-2 py-1.5 text-gray-600 truncate max-w-[60px]">{req.type}</td>
                      <td className="px-2 py-1.5">
                        <span className={`rounded px-1 py-0.5 font-bold ${
                          req.status === 'done'    ? 'bg-green-50 text-green-700'  :
                          req.status === 'error'   ? 'bg-red-50 text-red-600'     :
                          req.status === 'running' ? 'bg-blue-50 text-blue-600'   :
                          req.status === 'queued'  ? 'bg-yellow-50 text-yellow-700' :
                          'bg-gray-50 text-gray-600'
                        }`}>{req.status}</span>
                      </td>
                      <td className="px-2 py-1.5 text-red-500 truncate max-w-[50px]" title={req.error}>{req.error || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Open Flowboard link */}
          <div className="border-t border-gray-100 p-2 shrink-0">
            <a href="/flowboard/" target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-center gap-1.5 w-full rounded-lg bg-[#865916] py-2 text-xs font-bold text-white hover:bg-[#6d4712] transition-colors">
              <ExternalLink className="w-3.5 h-3.5" />Open Flowboard
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
